import React, { useEffect, useState } from 'react'
import './AppraisalsDetails.scss'
import { BreadCrumb, ModalWrapper } from 'Components'
import { ApStepOne, ApStepTwo, ApStepThree } from 'Pages/Frontend/Components'
import { appraisalService } from 'Services/appraisalService'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { AppraisalsType } from 'Models/appraisalsType'
import SaveIcon from 'Assets/Images/save-for-later.png'
import ApStepThreeOpen from 'Pages/Frontend/Components/Appraisals/ApStepThreeOpen'

export type AppraisalFormLMProps = {
  kpi: {
    metric: string
    target: number
    employee_score: number
    employee_comment: string
    manager_score?: number
    manager_comment?: string
  }[]
  coreValues: {
    question: string
    category: string
    employee_score: number
    manager_score?: number
  }[]
  isDraft: boolean
}

export type AppraisalFormProps = {
  kpi: {
    metric: string
    target: number
    employee_score: number
    employee_comment?: string
    manager_score?: number
    manager_comment?: string
  }[]
  coreValues: {
    question: string
    category: string
    employee_score: number
    manager_score?: number
  }[]
  personalAssessment: {
    category: string
    question: string
    answer: string
    manager_score?: number
  }[]
  lineManagerComment?: string
  isDraft?: boolean
}
const AppraisalsDetails = () => {
  const { apId, userId } = useParams<{ apId: string; userId: string }>()

  const [searchParams] = useSearchParams()
  const [request, setRequest] = useState(false)

  // Get a specific query parameter
  const step = searchParams.get('step')

  const [appraisal, setAppraisals] = useState<AppraisalsType>()
  // const currentStep = step ? parseInt(step) : 1; // Convert step to a number and default to 1 if invalid.

  const [currentStep, setCurrentStep] = useState(1)
  const navigate = useNavigate()

  const [showApprovalModal, setShowApprovalModal] = useState(false)

  const toggleApprovalModal = () => setShowApprovalModal(!showApprovalModal)

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useForm<AppraisalFormProps>()

  const onSubmit: SubmitHandler<AppraisalFormProps> = async (data, e) => {
    // console.log('Form Data:', data)
    setRequest(true)
    const action = (e?.nativeEvent as SubmitEvent)?.submitter as HTMLButtonElement

    const isDraft = action?.value === 'draft'
    await appraisalService
      .updateManager(apId, {
        kpi: data.kpi,
        coreValues: data.coreValues,
        lineManagerComment: data.lineManagerComment,
        isDraft,
      })
      .then(
        () => {
          setRequest(false)
          Swal.fire({
            icon: 'success',
            title: `${isDraft ? 'Saved' : 'Completed'}`,
            text: `Appraisal ${isDraft ? 'saved' : 'submitted'} successfully!`,
          })
          setTimeout(() => {
            navigate('/appraisals')
          }, 1000)
        },
        (error) => {
          setRequest(false)
          Swal.close()
          // console.log('productService error', error.response)
          const swalWithBootstrapButtons = Swal.mixin({
            customClass: {
              cancelButton: 'btn btn-danger',
            },
            buttonsStyling: false,
          })

          swalWithBootstrapButtons.fire({
            title: 'Oops!',
            text: error.response.data.error,
            icon: 'warning',
            showCancelButton: true,
            showConfirmButton: false,
            cancelButtonText: 'close',
            reverseButtons: true,
          })
        },
      )
  }

  const handleNext = async () => {
    const isValid = await trigger() // Validate the current form fields
    if (isValid) {
      setCurrentStep((prev) => prev + 1)
      navigate(`/appraisal-log/${apId}/${userId}?step=${currentStep + 1}`)
    } else {
      console.error('Form contains errors, cannot proceed to the next step.')
    }
  }

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1)
      navigate(`/appraisal-log/${apId}/${userId}?step=${currentStep - 1}`)
    }
  }

  const getAppraisals = async (apId: string) => {
    await appraisalService.getSingleAdmin(apId).then(
      (response) => {
        console.log('response 45', response.data.data.dataset[0])
        setAppraisals(response.data.data.dataset[0])
      },
      (error: { response: { data: { error: string } } }) => {
        console.log('appraisalService error', error.response)
      },
    )
  }

  useEffect(() => {
    if (apId) getAppraisals(apId)
  }, [apId])

  useEffect(() => {
    if (appraisal) {
      // Use the proper template literal type for the field path
      const pLength = appraisal.personalAssessment.length
      for (let x = 0; x < pLength; x++) {
        setValue(`personalAssessment.${x}.answer`, appraisal.personalAssessment[x].answer)
      }

      const kpLength = appraisal.kpi.length
      for (let x = 0; x < kpLength; x++) {
        setValue(`kpi.${x}.employee_score`, appraisal.kpi[x].employee_score)
        setValue(`kpi.${x}.employee_comment`, appraisal.kpi[x].employee_comment)
        setValue(`kpi.${x}.manager_score`, appraisal.kpi[x].manager_score)
        setValue(`kpi.${x}.manager_comment`, appraisal.kpi[x].manager_comment)
      }

      const cvLength = appraisal.coreValues.length
      for (let x = 0; x < cvLength; x++) {
        setValue(`coreValues.${x}.employee_score`, appraisal.coreValues[x].employee_score)
        setValue(`coreValues.${x}.manager_score`, appraisal.coreValues[x].manager_score)
      }
    }
  }, [appraisal])

  const [settings, setSettings] = useState<{
    id: number
    appraisalYear: number
    appraisalQuarter: number
    appraisalOpen: boolean
    payslipMonth: string
    payslipYear: any
    createdAt: string
  }>()

  const getAppraisalSettings = async () => {
    await appraisalService.settings().then(
      (res) => {
        // console.log('settings', res.data.data)
        setSettings(res.data.data)
      },
      (error) => {
        console.log(error)
      },
    )
  }

  useEffect(() => {
    getAppraisalSettings()
  }, [])
  const [activeAccordion, setActiveAccordion] = useState(1)
  const [inputError, setInputError] = useState(false)
  const [request2, setRequest2] = useState(false)

  const isOpen: boolean = appraisal?.status === 'AWAITING_LINE_MANAGER_REVIEW'

  return (
    <div className='AppraisalsDetails mb-5'>
      <div className='container'>
        <BreadCrumb
          title={'Q1’25 Appraisal Form'}
          parent={'Appraisal'}
          parentUrl={'appraisals'}
          showTitle={false}
        />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='container mt-5'>
          <div className='section-top mb-4'>
            <div className='section-title'>
              Q{settings?.appraisalQuarter} {settings?.appraisalYear} Appraisal Form
            </div>
            <div className='section-description'>
              {step}
              SECTION A ({step === '' ? 1 : step} of 3)
            </div>
          </div>
          {currentStep === 1 && (
            <ApStepOne
              register={register}
              errors={errors}
              isManager={true}
              activeAccordion={activeAccordion}
              setActiveAccordion={setActiveAccordion}
              inputError={inputError}
              setInputError={setInputError}
            />
          )}
          {currentStep === 2 && (
            <ApStepTwo
              register={register}
              errors={errors}
              isManager={true}
              isAdmin={false}
              isDisabled={true}
              watch={watch}
              employeeId={userId}
              control={control}
            />
          )}

          {currentStep === 3 && isOpen && (
            <ApStepThreeOpen
              register={register}
              errors={errors}
              watch={watch}
              isManager={true}
              isAdmin={false}
              isDisabled={true}
              control={control}
            />
          )}

          {currentStep === 3 && !isOpen && (
            <ApStepThree
              register={register}
              errors={errors}
              isManager={true}
              isAdmin={false}
              isDisabled={true}
              watch={watch}
            />
          )}

          {currentStep === 3 && (
            <div className='card border-0'>
              <div className='card-body'>
                <div className='approvalmodal'>
                  <div className='mb-3'>kindly comment on the performance of the employee.</div>
                  <div className='form-group mb-3 mt-4'>
                    <label htmlFor='comment'>LineManager&rsquo;s Comment</label>

                    <textarea
                      {...register('lineManagerComment', {
                        required: 'Field is required',
                      })}
                      id='appraisalQuarter'
                      className='form-control'
                      placeholder='Comment goes here'
                    >
                      {' '}
                    </textarea>
                    {errors.lineManagerComment && (
                      <small className='text-danger'>{errors.lineManagerComment.message}</small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className='row justify-content-end mt-3'>
            <div className='col-sm-12 col-md-6 col-xl-6 profile-action-ap'>
              {currentStep === 3 && (
                <button
                  name='action'
                  value='draft'
                  className='btn m-1 btn-navigate d-flex align-items-center'
                  type='submit'
                  disabled={request}
                >
                  <span className='me-2 fs-5'>
                    {' '}
                    <img className='save-icon' src={SaveIcon} alt='icon' />
                  </span>
                  Save for Later{' '}
                  {request && <span className='spinner-border spinner-border-sm'></span>}
                </button>
              )}
            </div>
            <div className='col-sm-12 col-md-6 col-xl-6 text-end'>
              {currentStep > 1 && (
                <button
                  type='button'
                  className='btn btn-default m-1 btn-navigate'
                  onClick={handlePrev}
                >
                  Prev
                </button>
              )}

              {currentStep < 3 && (
                <button
                  type='button'
                  className='btn btn-default m-1 btn-navigate'
                  onClick={handleNext}
                >
                  Next
                </button>
              )}

              {currentStep === 3 && (
                <button
                  name='action'
                  value='submit'
                  className='btn btn-default m-1 btn-navigate'
                  type='submit'
                  disabled={request}
                  onClick={() => toggleApprovalModal()}
                >
                  Submit {request && <span className='spinner-border spinner-border-sm'></span>}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* <ModalWrapper
          showModal={showApprovalModal}
          closeModal={toggleApprovalModal}
          title="Line Manager's Comment"
        ></ModalWrapper> */}
      </form>
    </div>
  )
}

export default AppraisalsDetails
